/**
 * Rage Nation Modular Blocks - Interactive JavaScript
 * Handles countdown timers and other interactive product page blocks
 */

(function() {
  'use strict';

  // ============================================================
  // COUNTDOWN TIMER
  // ============================================================
  function initCountdownTimers() {
    const timers = document.querySelectorAll('.rn-countdown-timer');

    timers.forEach(timer => {
      const endDateStr = timer.getAttribute('data-end-date');
      if (!endDateStr) return;

      // Parse end date (format: YYYY-MM-DD HH:MM)
      const endDate = new Date(endDateStr.replace(' ', 'T'));

      // Get expiration behavior setting
      const expirationBehavior = timer.getAttribute('data-expiration-behavior') || 'hide';

      // Get display elements
      const display = timer.querySelector('.rn-countdown-timer__display');
      const expiredMsg = timer.querySelector('.rn-countdown-timer__expired');
      const daysEl = timer.querySelector('[data-unit="days"]');
      const hoursEl = timer.querySelector('[data-unit="hours"]');
      const minutesEl = timer.querySelector('[data-unit="minutes"]');
      const secondsEl = timer.querySelector('[data-unit="seconds"]');

      function updateCountdown() {
        const now = new Date().getTime();
        const distance = endDate - now;

        // Check if countdown has expired
        if (distance < 0) {
          if (expirationBehavior === 'hide') {
            // Hide the entire timer
            timer.style.display = 'none';
          } else if (expirationBehavior === 'show_message') {
            // Hide countdown display and show expired message
            if (display) display.style.display = 'none';
            if (expiredMsg) expiredMsg.style.display = 'block';
          }
          return;
        }

        // Calculate time units
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        // Update display with zero-padding
        if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
        if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
        if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
        if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
      }

      // Initial update
      updateCountdown();

      // Update every second
      setInterval(updateCountdown, 1000);
    });
  }

  // ============================================================
  // IMAGE GALLERY LIGHTBOX
  // ============================================================
  function initImageGalleryLightbox() {
    // Simple lightbox for image galleries
    const galleries = document.querySelectorAll('.rn-image-gallery-grid');

    galleries.forEach(gallery => {
      const lightboxLinks = gallery.querySelectorAll('[data-lightbox]');

      lightboxLinks.forEach(link => {
        link.addEventListener('click', function(e) {
          e.preventDefault();

          // Create lightbox overlay
          const overlay = document.createElement('div');
          overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.95);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 40px;
          `;

          // Create image
          const img = document.createElement('img');
          img.src = this.href;
          img.style.cssText = `
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            cursor: default;
          `;

          // Prevent image click from closing
          img.addEventListener('click', function(e) {
            e.stopPropagation();
          });

          // Close on overlay click
          overlay.addEventListener('click', function() {
            document.body.removeChild(overlay);
            document.body.style.overflow = '';
          });

          // Close on ESC key
          const closeOnEsc = function(e) {
            if (e.key === 'Escape') {
              document.body.removeChild(overlay);
              document.body.style.overflow = '';
              document.removeEventListener('keydown', closeOnEsc);
            }
          };
          document.addEventListener('keydown', closeOnEsc);

          overlay.appendChild(img);
          document.body.appendChild(overlay);
          document.body.style.overflow = 'hidden';
        });
      });
    });
  }

  // ============================================================
  // URGENCY MESSAGE ANIMATIONS
  // ============================================================
  function initUrgencyMessages() {
    const messages = document.querySelectorAll('.rn-urgency-message');

    messages.forEach(message => {
      // Add subtle pulse animation for gradient style
      if (message.classList.contains('rn-urgency-message--gradient')) {
        const inner = message.querySelector('div');
        if (inner) {
          inner.style.backgroundImage = `linear-gradient(90deg, ${getComputedStyle(inner).backgroundColor}, rgba(255,255,255,0.1))`;
          inner.style.backgroundSize = '200% 100%';
          inner.style.animation = 'rn-gradient-shift 3s ease infinite';
        }
      }

      // Add pulse for bold style
      if (message.classList.contains('rn-urgency-message--bold')) {
        message.style.animation = 'rn-pulse-subtle 2s ease-in-out infinite';
      }
    });

    // Add keyframe animations if they don't exist
    if (!document.getElementById('rn-urgency-animations')) {
      const style = document.createElement('style');
      style.id = 'rn-urgency-animations';
      style.textContent = `
        @keyframes rn-gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes rn-pulse-subtle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // ============================================================
  // VIDEO EMBED ENHANCEMENTS
  // ============================================================
  function initVideoEmbeds() {
    const videos = document.querySelectorAll('.rn-video-embed iframe');

    videos.forEach(video => {
      // Lazy load videos on scroll
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const src = video.getAttribute('src');
              if (src && !video.hasAttribute('data-loaded')) {
                video.setAttribute('data-loaded', 'true');
                // Video will load automatically
              }
              observer.unobserve(entry.target);
            }
          });
        });

        observer.observe(video);
      }
    });
  }

  // ============================================================
  // STOCK INDICATOR ANIMATIONS
  // ============================================================
  function initStockIndicators() {
    const indicators = document.querySelectorAll('.rn-stock-indicator');

    indicators.forEach(indicator => {
      const progressBar = indicator.querySelector('[style*="transition"]');

      if (progressBar && 'IntersectionObserver' in window) {
        // Animate progress bar when it comes into view
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Trigger animation by temporarily setting width to 0 then back
              const currentWidth = progressBar.style.width;
              progressBar.style.transition = 'none';
              progressBar.style.width = '0%';

              setTimeout(() => {
                progressBar.style.transition = 'width 1s ease';
                progressBar.style.width = currentWidth;
              }, 100);

              observer.unobserve(entry.target);
            }
          });
        });

        observer.observe(indicator);
      }
    });
  }

  // ============================================================
  // VARIANT STOCK INDICATOR WEB COMPONENT
  // ============================================================
  class VariantStockIndicator extends HTMLElement {
    constructor() {
      super();
      this.threshold = parseInt(this.dataset.threshold) || 10;
      this.max = parseInt(this.dataset.max) || 100;
      this.messageTemplate = this.dataset.messageTemplate || 'Only {quantity} left in stock!';
      this.showProgress = this.dataset.showProgress === 'true';

      // Get stock data
      const stockDataEl = document.querySelector('[data-stock-data]');
      this.stockData = stockDataEl ? JSON.parse(stockDataEl.textContent) : {};

      // Get form
      const formId = this.getAttribute('form');
      this.form = formId ? document.getElementById(formId) : null;

      if (this.form) {
        this.form.addEventListener('variant:change', this.handleVariantChange.bind(this));
      }
    }

    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant || !this.stockData[variant.id]) return;

      const stock = this.stockData[variant.id];
      const inventory = stock.inventory;

      // Show/hide based on threshold
      if (inventory > 0 && inventory <= this.threshold && stock.available) {
        this.style.display = 'block';
        this.updateDisplay(inventory);
      } else {
        this.style.display = 'none';
      }
    }

    updateDisplay(quantity) {
      // Update message
      const messageEl = this.querySelector('.rn-stock-indicator__message');
      if (messageEl) {
        messageEl.textContent = this.messageTemplate.replace('{quantity}', quantity);
      }

      // Update progress bar
      if (this.showProgress) {
        const progressEl = this.querySelector('.rn-stock-indicator__progress');
        if (progressEl) {
          const percent = Math.min((quantity / this.max) * 100, 100);
          progressEl.style.width = percent + '%';
        }
      }
    }
  }

  // Register the custom element
  if ('customElements' in window) {
    customElements.define('variant-stock-indicator', VariantStockIndicator);
  }

  // ============================================================
  // ADVANCED VARIANT SELECTOR WEB COMPONENT
  // ============================================================
  class AdvancedVariantSelector extends HTMLElement {
    constructor() {
      super();

      // Get settings from data attributes
      this.productId = this.dataset.productId;
      this.selectorStyle = this.dataset.selectorStyle || 'pills';
      this.selectorSize = this.dataset.selectorSize || 'medium';
      this.hideSoldOut = this.dataset.hideSoldOut === 'true';
      this.showStock = this.dataset.showStock === 'true';
      this.stockThreshold = parseInt(this.dataset.stockThreshold) || 10;
      this.enableImageSwatches = this.dataset.enableImageSwatches === 'true';
      this.showTooltips = this.dataset.showTooltips === 'true';

      // Parse variant data
      const variantDataEl = this.querySelector('[data-variant-data]');
      this.variantData = variantDataEl ? JSON.parse(variantDataEl.textContent) : null;

      // Get form reference
      const formId = this.getAttribute('form');
      this.form = formId ? document.getElementById(formId) : null;

      // Track selected options
      this.selectedOptions = {};

      // Initialize
      if (this.variantData) {
        this.init();
      }
    }

    init() {
      // Set initial selected options from variant data
      const selectedVariant = this.variantData.variants.find(v => v.id === this.variantData.selected_variant_id);
      if (selectedVariant) {
        this.selectedOptions = {
          0: selectedVariant.option1,
          1: selectedVariant.option2,
          2: selectedVariant.option3
        };
      }

      // Add click handlers to all option buttons
      const optionGroups = this.querySelectorAll('.rn-variant-options');
      optionGroups.forEach(group => {
        const optionIndex = parseInt(group.dataset.optionIndex);
        const buttons = group.querySelectorAll('.rn-variant-option');

        buttons.forEach(button => {
          button.addEventListener('click', (e) => this.handleOptionClick(e, optionIndex));
        });
      });

      // Update availability on load
      this.updateAvailability();
    }

    handleOptionClick(e, optionIndex) {
      const button = e.currentTarget;
      const value = button.dataset.value;

      // Don't do anything if sold out
      if (button.dataset.available === 'false') {
        return;
      }

      // Update selected options
      this.selectedOptions[optionIndex] = value;

      // Update UI
      this.updateSelectedState(optionIndex, value);

      // Find matching variant
      const matchingVariant = this.findMatchingVariant();

      if (matchingVariant) {
        // Update availability of other options
        this.updateAvailability();

        // Trigger variant change event for other components
        if (this.form) {
          const event = new CustomEvent('variant:change', {
            detail: {
              variant: matchingVariant,
              productId: this.productId
            },
            bubbles: true
          });
          this.form.dispatchEvent(event);
        }

        // Update URL if supported
        this.updateURL(matchingVariant.id);
      }
    }

    updateSelectedState(optionIndex, selectedValue) {
      const group = this.querySelector(`[data-option-index="${optionIndex}"]`);
      if (!group) return;

      const buttons = group.querySelectorAll('.rn-variant-option');
      buttons.forEach(button => {
        const isSelected = button.dataset.value === selectedValue;

        if (isSelected) {
          button.classList.add('selected');
          // Apply selected styles
          button.style.backgroundColor = getComputedStyle(this).getPropertyValue('--selected-bg') || '#e2af00';
          button.style.color = getComputedStyle(this).getPropertyValue('--selected-text') || '#000000';
        } else {
          button.classList.remove('selected');
          // Revert to available styles
          if (button.dataset.available === 'true') {
            button.style.backgroundColor = getComputedStyle(this).getPropertyValue('--available-bg') || '#1a1a1a';
            button.style.color = getComputedStyle(this).getPropertyValue('--available-text') || '#eaeaea';
          }
        }
      });
    }

    findMatchingVariant() {
      return this.variantData.variants.find(variant => {
        return (
          (!this.selectedOptions[0] || variant.option1 === this.selectedOptions[0]) &&
          (!this.selectedOptions[1] || variant.option2 === this.selectedOptions[1]) &&
          (!this.selectedOptions[2] || variant.option3 === this.selectedOptions[2])
        );
      });
    }

    updateAvailability() {
      // For each option group, update which options are available given current selections
      const optionGroups = this.querySelectorAll('.rn-variant-options');

      optionGroups.forEach(group => {
        const optionIndex = parseInt(group.dataset.optionIndex);
        const buttons = group.querySelectorAll('.rn-variant-option');

        buttons.forEach(button => {
          const value = button.dataset.value;

          // Check if this value leads to any available variants
          const testOptions = {...this.selectedOptions};
          testOptions[optionIndex] = value;

          const hasAvailableVariant = this.variantData.variants.some(variant => {
            const matches = (
              (!testOptions[0] || variant.option1 === testOptions[0]) &&
              (!testOptions[1] || variant.option2 === testOptions[1]) &&
              (!testOptions[2] || variant.option3 === testOptions[2])
            );
            return matches && variant.available;
          });

          // Update button state
          if (!hasAvailableVariant) {
            button.classList.add('soldout');
            button.dataset.available = 'false';
            button.disabled = true;
            button.style.opacity = '0.5';
            button.style.textDecoration = 'line-through';
            button.style.cursor = 'not-allowed';
          } else {
            button.classList.remove('soldout');
            button.dataset.available = 'true';
            button.disabled = false;
            button.style.opacity = '1';
            button.style.textDecoration = 'none';
            button.style.cursor = 'pointer';
          }

          // Hide if setting is enabled
          if (this.hideSoldOut && !hasAvailableVariant) {
            button.style.display = 'none';
          } else {
            button.style.display = '';
          }
        });
      });
    }

    updateURL(variantId) {
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        history.replaceState({}, '', url.toString());
      }
    }
  }

  // Register the advanced variant selector
  if ('customElements' in window) {
    customElements.define('rn-variant-selector', AdvancedVariantSelector);
  }

  // ============================================================
  // INITIALIZE ALL ON PAGE LOAD
  // ============================================================
  function init() {
    initCountdownTimers();
    initImageGalleryLightbox();
    initUrgencyMessages();
    initVideoEmbeds();
    initStockIndicators();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify theme editor updates
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
